import React, { useState, useEffect } from 'react';
import { Container, Form, Card, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../service/api.js';
import { useToast } from '../../contexts/ToastContext';
import './AnswerQuestions.css';

const AVATAR_TONES = ['purple', 'pink', 'green', 'blue', 'orange'];

const getInitials = (firstName = '', lastName = '') => {
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  return 'NA';
};

const avatarTone = (id = '') => {
  const hash = String(id).split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return AVATAR_TONES[hash % AVATAR_TONES.length];
};

const statusMeta = (status = '') => {
  const key = String(status || '').toLowerCase();
  if (key === 'completed') {
    return { label: 'Completed', tone: 'completed', icon: 'bi-check-circle-fill' };
  }
  if (key === 'unreachable') {
    return { label: 'Unreachable', tone: 'unreachable', icon: 'bi-telephone-fill' };
  }
  if (key === 'call_back' || key === 'callback') {
    return { label: 'Call Back', tone: 'callback', icon: 'bi-telephone-fill' };
  }
  return { label: status || 'Pending', tone: 'pending', icon: 'bi-circle' };
};

const AnswerQuestions = () => {
  const { token: authToken } = useAuth();
  const navigate = useNavigate();
  const { showError } = useToast();

  const [operators, setOperators] = useState([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState('');
  const [voterGroups, setVoterGroups] = useState([]);
  const [loadingOperators, setLoadingOperators] = useState(true);
  const [loadingResults, setLoadingResults] = useState(false);

  useEffect(() => {
    const fetchOperators = async () => {
      try {
        const res = await api.get('/api/admin/telecallers');
        setOperators(res.data || []);
      } catch {
        showError('Failed to fetch telecallers');
      } finally {
        setLoadingOperators(false);
      }
    };

    if (authToken) fetchOperators();
  }, [authToken]);

  useEffect(() => {
    if (!selectedOperatorId) return;

    const fetchData = async () => {
      try {
        setLoadingResults(true);

        const res = await api.get(`/api/answers/operator/${selectedOperatorId}`);

        const grouped = {};

        res.data.forEach(a => {
          const vid = a.voter._id;

          if (!grouped[vid]) {
            grouped[vid] = {
              voter: a.voter,
              answers: [],
              lastRecorded: a.createdAt
            };
          }

          if (a.question) {
            grouped[vid].answers.push({
              question: a.question,
              answerText: a.answerText,
              recordedAt: a.createdAt,
              type: a.type || 'survey'
            });
          }

          if (new Date(a.createdAt) > new Date(grouped[vid].lastRecorded)) {
            grouped[vid].lastRecorded = a.createdAt;
          }
        });

        setVoterGroups(
          Object.values(grouped).sort(
            (a, b) => new Date(b.lastRecorded) - new Date(a.lastRecorded)
          )
        );

      } catch {
        showError('Failed to load operator data');
      } finally {
        setLoadingResults(false);
      }
    };

    fetchData();
  }, [selectedOperatorId, authToken]);

  return (
    <div className="tp-page">
      <Container fluid className="tp-container">
        <div className="tp-header">
          <h1>Telecaller Performance</h1>
          <p>Monitor operator activity</p>
        </div>

        <div className="tp-selector-row">
          <Card className="tp-select-card">
            <Card.Body>
              <Form.Label className="tp-select-label">Select Telecaller</Form.Label>
              {loadingOperators ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Form.Select
                  className="tp-select"
                  value={selectedOperatorId}
                  onChange={(e) => {
                    setSelectedOperatorId(e.target.value);
                  }}
                >
                  <option value="">-- Select --</option>
                  {operators.map(op => (
                    <option key={op._id} value={op._id}>
                      {op.username}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Card.Body>
          </Card>

          <Card className="tp-count-card">
            <Card.Body>
              <div className="tp-count-icon" aria-hidden="true">
                <i className="bi bi-people-fill" />
              </div>
              <div>
                <div className="tp-count-value">{voterGroups.length}</div>
                <div className="tp-count-label">Voters Attended</div>
              </div>
            </Card.Body>
          </Card>
        </div>

        {!selectedOperatorId ? (
          <Card className="tp-empty-card">Select an operator to view reports</Card>
        ) : loadingResults ? (
          <div className="tp-loading">
            <Spinner animation="border" />
          </div>
        ) : voterGroups.length === 0 ? (
          <Card className="tp-empty-card">No Data Found</Card>
        ) : (
          <div>
            <h2 className="tp-section-title">
              <i className="bi bi-file-earmark-text" aria-hidden="true" />
              Attended Target Reports
            </h2>

            <div className="tp-voter-grid">
              {voterGroups.map(group => {
                const status = statusMeta(group.voter.callStatus);
                const initials = getInitials(group.voter.firstName, group.voter.lastName);

                return (
                  <button
                    type="button"
                    key={group.voter._id}
                    className="tp-voter-card"
                    onClick={() =>
                      navigate(`/admin/voter-details/${group.voter._id}`, {
                        state: group
                      })
                    }
                  >
                    <div className="tp-voter-main">
                      <div className={`tp-avatar tp-avatar-${avatarTone(group.voter._id)}`}>
                        {initials}
                      </div>
                      <div className="tp-voter-copy">
                        <div className="tp-voter-name">
                          {group.voter.firstName} {group.voter.lastName}
                        </div>
                        <div className="tp-voter-epic">
                          EPIC: {group.voter.epicNo || 'N/A'}
                        </div>
                      </div>
                    </div>
                    <span className={`tp-status tp-status-${status.tone}`}>
                      <i className={`bi ${status.icon}`} aria-hidden="true" />
                      {status.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default AnswerQuestions;
